// ShareProject - Compartir proyecto con otros usuarios (plan Pro)
import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import './ShareProject.css';

const ShareProject = () => {
    const { projectId } = useParams();
    const { user } = useAuth();
    const [project, setProject] = useState(null);
    const [members, setMembers] = useState([]);
    const [invitations, setInvitations] = useState([]);
    const [inviteEmail, setInviteEmail] = useState('');
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [message, setMessage] = useState('');

    useEffect(() => {
        if (!user || !projectId) return;
        if (projectId === 'fixarr') {
            setProject(null);
            setLoading(false);
            return;
        }
        loadProjectAndMembers();
    }, [user, projectId]);

    const loadProjectAndMembers = async () => {
        try {
            const { data: projectData, error: projectError } = await supabase
                .from('projects')
                .select('id, name, user_id')
                .eq('id', projectId)
                .single();

            if (projectError || !projectData) {
                setProject(null);
                setLoading(false);
                return;
            }
            if (projectData.user_id !== user.id) {
                setMessage('Solo el creador del proyecto puede invitar a otros.');
                setProject(projectData);
                setLoading(false);
                return;
            }

            setProject(projectData);

            const { data: membersData } = await supabase
                .from('project_members')
                .select('id, user_id, role, accepted_at')
                .eq('project_id', projectId);
            setMembers(membersData || []);

            const { data: invData } = await supabase
                .from('project_invitations')
                .select('id, email, role, created_at, expires_at, accepted_at')
                .eq('project_id', projectId)
                .is('accepted_at', null);
            setInvitations(invData || []);
        } catch (e) {
            console.error(e);
            setMessage('Error al cargar el proyecto.');
        } finally {
            setLoading(false);
        }
    };

    const handleInvite = async (e) => {
        e.preventDefault();
        if (!inviteEmail.trim() || !project || projectId === 'fixarr') return;
        setSending(true);
        setMessage('');
        try {
            const token = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
            const expiresAt = new Date();
            expiresAt.setDate(expiresAt.getDate() + 7);

            const { error } = await supabase
                .from('project_invitations')
                .insert({
                    project_id: projectId,
                    email: inviteEmail.trim().toLowerCase(),
                    role: 'editor',
                    token,
                    invited_by: user.id,
                    expires_at: expiresAt.toISOString(),
                });

            if (error) {
                if (error.code === '23505') setMessage('Ya hay una invitación pendiente para ese email.');
                else setMessage(error.message || 'Error al enviar invitación.');
                setSending(false);
                return;
            }
            setInviteEmail('');
            setMessage('Invitación creada. Envía este enlace al usuario: ' + window.location.origin + '/invite/' + token);
            loadProjectAndMembers();
        } catch (e) {
            setMessage(e.message || 'Error al invitar.');
        } finally {
            setSending(false);
        }
    };

    const revokeInvitation = async (invId) => {
        const { error } = await supabase.from('project_invitations').delete().eq('id', invId);
        if (!error) loadProjectAndMembers();
    };

    if (loading) {
        return (
            <div className="share-project-container">
                <div className="share-project-loading">Cargando...</div>
            </div>
        );
    }

    if (projectId === 'fixarr' || !project) {
        return (
            <div className="share-project-container">
                <div className="share-project-card">
                    <h1>Compartir proyecto</h1>
                    <p>Este proyecto (FIXARR) es de ejemplo y no se puede compartir. Crea tu propio proyecto desde el dashboard para usar la función de compartir.</p>
                    <Link to="/dashboard" className="share-project-back">← Volver al Dashboard</Link>
                </div>
            </div>
        );
    }

    return (
        <div className="share-project-container">
            <div className="share-project-card">
                <div className="share-project-header">
                    <h1>Compartir: {project.name}</h1>
                    <Link to={`/project/${projectId}`} className="share-project-back">← Volver al proyecto</Link>
                </div>

                {message && <div className="share-project-message">{message}</div>}

                <section className="share-project-section">
                    <h2>Invitar por email</h2>
                    <p className="share-project-hint">Hasta 10 usuarios por proyecto en plan Pro.</p>
                    <form onSubmit={handleInvite} className="share-project-form">
                        <input
                            type="email"
                            placeholder="email@ejemplo.com"
                            value={inviteEmail}
                            onChange={(e) => setInviteEmail(e.target.value)}
                            required
                            disabled={sending}
                        />
                        <button type="submit" disabled={sending}>
                            {sending ? 'Enviando...' : 'Enviar invitación'}
                        </button>
                    </form>
                </section>

                <section className="share-project-section">
                    <h2>Invitaciones pendientes</h2>
                    {invitations.length === 0 ? (
                        <p className="share-project-empty">Ninguna invitación pendiente.</p>
                    ) : (
                        <ul className="share-project-list">
                            {invitations.map((inv) => (
                                <li key={inv.id}>
                                    <span>{inv.email}</span>
                                    <span className="share-project-role">{inv.role}</span>
                                    <button type="button" className="share-project-revoke" onClick={() => revokeInvitation(inv.id)}>
                                        Revocar
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </section>

                <section className="share-project-section">
                    <h2>Miembros del proyecto</h2>
                    <p className="share-project-empty">Tú eres el propietario. Los usuarios que acepten la invitación aparecerán aquí.</p>
                    {members.length > 0 && (
                        <ul className="share-project-list">
                            {members.map((m) => (
                                <li key={m.id}>
                                    <span>Usuario {m.user_id?.slice(0, 8)}...</span>
                                    <span className="share-project-role">{m.role}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </section>
            </div>
        </div>
    );
};

export default ShareProject;
